/**
 * PCB Layout Nodes action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import type {
    AnyPcbBakedNode,
    AnyPcbNode,
    DocumentService,
    IPcbLayoutNodesRuleSetsMap,
    PcbNode,
    PcbNodesMap,
    PcbNodeTypes,
} from "@buildwithflux/core";
import {EditorModes} from "@buildwithflux/core";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import {getUpdatedElementNodePositions} from "modules/document/state/actionCreators/pcbLayoutNodeActions";
import {getAllBakedNodes} from "modules/stores/pcb/utils";
import type {UsePersistedDocumentUiStore} from "modules/stores/ui";

import type {DocumentStore} from "../document";

/**
 * Helper function to dispatch addPolygon action with context gathering
 *
 * This function gathers the necessary context (editorMode, nets, pcbLayoutNodes) and
 * dispatches the addPolygon action via DocumentStore. It's used by both the
 * useAddPolygon hook (for React contexts) and events.ts (for non-React contexts).
 *
 * @param node - The polygon node to add
 * @param documentStore - The Zustand document store
 * @param usePersistedDocumentUiStore - The persisted document UI store
 * @param documentService - The document service for accessing baked nodes
 */
export function dispatchAddPolygon(
    node: PcbNode<PcbNodeTypes.polygon>,
    documentStore: DocumentStore,
    usePersistedDocumentUiStore: UsePersistedDocumentUiStore,
    documentService: DocumentService,
): void {
    // Get document nets from Zustand store
    const document = documentStore.getState().document;
    if (!document?.nets) {
        throw new Error("Document nets not found in state");
    }

    // Get editorMode from persisted UI store
    const {editorMode} = usePersistedDocumentUiStore.getState();

    // Get all baked PCB layout nodes
    const pcbLayoutNodes = getAllBakedNodes(documentService);

    // Dispatch the action directly to Zustand via DocumentStore
    const nodeNet = node.parentUid;
    const allPinsInNet = Object.keys(document.nets[nodeNet]?.pins ?? {});
    const updatedNodePositions = getUpdatedElementNodePositions(allPinsInNet, pcbLayoutNodes);
    documentStore.getState().addPolygon(node, updatedNodePositions, editorMode === EditorModes.pcb);
}

/**
 * Hook to get the updateRouteSegments action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to update route segments
 */
export function useUpdateRouteSegments(): (updates: Record<string, PcbNode<PcbNodeTypes.routeSegment>>) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (updates: Record<string, PcbNode<PcbNodeTypes.routeSegment>>) => {
            documentStore.getState().updateRouteSegments(updates);
        },
        [documentStore],
    );
}

/**
 * Hook to get the addNodes action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * This hook gathers the editorMode at execution time and dispatches the addNodes
 * action directly, eliminating the need for the Redux thunk.
 *
 * @returns A function to add PCB layout nodes
 */
export function useAddNodes(): (nodes: PcbNodesMap<AnyPcbNode>) => void {
    const {documentStore, usePersistedDocumentUiStore} = useFluxServices();

    return useCallback(
        (nodes: PcbNodesMap<AnyPcbNode>) => {
            const {editorMode} = usePersistedDocumentUiStore.getState();
            documentStore.getState().addNodes(nodes, editorMode === EditorModes.pcb);
        },
        [documentStore, usePersistedDocumentUiStore],
    );
}

/**
 * Hook to get the removeNodes action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * This action can either entirely remove PCB nodes from their UID, or "toggle" them
 * by using a rule set with an !important rule. If you want to toggle the PCB node,
 * pass them as baked nodes in the `toggleBakedNodes` and the action will create/remove
 * a rule set for it.
 *
 * @returns A function to remove PCB layout nodes
 */
export function useRemoveNodes(): (
    removeNodeUids: string[],
    toggleBakedNodes?: AnyPcbBakedNode[],
    updatedNodesRulesSetsMap?: IPcbLayoutNodesRuleSetsMap,
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (
            removeNodeUids: string[],
            toggleBakedNodes: AnyPcbBakedNode[] = [],
            updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap = {},
        ) => {
            documentStore.getState().removeNodes(removeNodeUids, toggleBakedNodes, updatedNodesRulesSetsMap);
        },
        [documentStore],
    );
}

/**
 * Hook to get the addPolygon action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * This hook gathers the necessary context (editorMode, nets, pcbLayoutNodes) at
 * execution time and dispatches the addPolygon action directly, eliminating the
 * need for the Redux thunk.
 *
 * @returns A function to add a PCB polygon
 */
export function useAddPolygon(): (node: PcbNode<PcbNodeTypes.polygon>) => void {
    const {documentStore, usePersistedDocumentUiStore, documentService} = useFluxServices();

    return useCallback(
        (node: PcbNode<PcbNodeTypes.polygon>) => {
            dispatchAddPolygon(node, documentStore, usePersistedDocumentUiStore, documentService);
        },
        [documentStore, usePersistedDocumentUiStore, documentService],
    );
}

/**
 * Factory function to create PCB layout node actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch PCB layout node actions.
 *
 * @param documentStore - The Zustand document store
 * @param usePersistedDocumentUiStore - The persisted document UI store hook
 * @param documentService - The document service for accessing baked nodes
 * @returns An object with PCB layout node action methods
 */
export function createPcbLayoutNodeActions(
    documentStore: DocumentStore,
    usePersistedDocumentUiStore: UsePersistedDocumentUiStore,
    documentService: DocumentService,
) {
    return {
        addNodes: (nodes: PcbNodesMap<AnyPcbNode>) => {
            const {editorMode} = usePersistedDocumentUiStore.getState();
            documentStore.getState().addNodes(nodes, editorMode === EditorModes.pcb);
        },
        updateRouteSegments: (updates: Record<string, PcbNode<PcbNodeTypes.routeSegment>>) => {
            documentStore.getState().updateRouteSegments(updates);
        },
        removeNodes: (
            removeNodeUids: string[],
            toggleBakedNodes: AnyPcbBakedNode[] = [],
            updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap = {},
        ) => {
            documentStore.getState().removeNodes(removeNodeUids, toggleBakedNodes, updatedNodesRulesSetsMap);
        },
        addPolygon: (node: PcbNode<PcbNodeTypes.polygon>) => {
            dispatchAddPolygon(node, documentStore, usePersistedDocumentUiStore, documentService);
        },
    };
}
